import { useState, useEffect } from "react";
import { adminAPI } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, Phone, UserX, UserCheck, ChevronLeft, ChevronRight } from "lucide-react";

export function AdminOtpDropoffsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);

  const load = async (p) => {
    setLoading(true);
    try {
      const res = await adminAPI.otpDropoffs({ page: p, limit: 25 });
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(page); }, [page]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  if (!data) return <p className="text-sm text-slate-500 text-center py-8">Failed to load data</p>;

  const conversionRate = data.total_otp_requests > 0
    ? ((data.completed_logins / data.total_otp_requests) * 100).toFixed(1)
    : 0;

  return (
    <div className="space-y-6" data-testid="admin-otp-dropoffs">
      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white border rounded-lg p-4">
          <p className="text-xs text-slate-500 mb-1">Total OTP Requests</p>
          <p className="text-2xl font-bold text-slate-800">{data.total_otp_requests}</p>
        </div>
        <div className="bg-white border rounded-lg p-4">
          <p className="text-xs text-slate-500 mb-1">Completed Logins</p>
          <p className="text-2xl font-bold text-emerald-600">{data.completed_logins}</p>
        </div>
        <div className="bg-white border rounded-lg p-4">
          <p className="text-xs text-slate-500 mb-1">Drop-offs</p>
          <p className="text-2xl font-bold text-red-600">{data.dropoffs}</p>
        </div>
        <div className="bg-white border rounded-lg p-4">
          <p className="text-xs text-slate-500 mb-1">Conversion Rate</p>
          <p className="text-2xl font-bold text-blue-600">{conversionRate}%</p>
          <p className="text-[10px] text-slate-400">Drop-off: {data.dropoff_rate}%</p>
        </div>
      </div>

      {/* Drop-off users table */}
      <div className="bg-white border rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <div className="flex items-center gap-2">
            <UserX className="h-4 w-4 text-red-500" />
            <h3 className="text-sm font-semibold text-slate-700">Users Who Never Completed Login</h3>
          </div>
          <Badge variant="secondary" className="text-[10px]">{data.dropoffs} users</Badge>
        </div>

        {data.users.length === 0 ? (
          <div className="py-12 text-center">
            <UserCheck className="h-8 w-8 text-emerald-400 mx-auto mb-2" />
            <p className="text-sm text-slate-500">No drop-offs yet. All OTP requests completed login.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs text-slate-500">
                  <tr>
                    <th className="text-left px-4 py-2.5 font-medium">Name</th>
                    <th className="text-left px-4 py-2.5 font-medium">Phone</th>
                    <th className="text-left px-4 py-2.5 font-medium">OTP Attempts</th>
                    <th className="text-left px-4 py-2.5 font-medium">First Request</th>
                    <th className="text-left px-4 py-2.5 font-medium">Last Request</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {data.users.map((u, i) => (
                    <tr key={i} className="hover:bg-slate-50">
                      <td className="px-4 py-2.5 text-slate-700">{u.first_name || "—"}</td>
                      <td className="px-4 py-2.5">
                        <span className="flex items-center gap-1.5 text-slate-600">
                          <Phone className="h-3 w-3 text-slate-400" />
                          {u.phone}
                        </span>
                      </td>
                      <td className="px-4 py-2.5">
                        <Badge variant={u.request_count > 2 ? "destructive" : "secondary"} className="text-[10px]">
                          {u.request_count}x
                        </Badge>
                      </td>
                      <td className="px-4 py-2.5 text-slate-500">{u.created_at || "—"}</td>
                      <td className="px-4 py-2.5 text-slate-500">{u.last_requested_at || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {data.total_pages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t">
                <p className="text-xs text-slate-500">Page {data.page} of {data.total_pages}</p>
                <div className="flex gap-2">
                  <Button
                    variant="outline" size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage(p => p - 1)}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline" size="sm"
                    disabled={page >= data.total_pages}
                    onClick={() => setPage(p => p + 1)}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
